import { useState } from "react"
import Dialog from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useReviewBillingAccountMutation } from "@/v2-backoffice-client/license/account/view/license.account.review.mutation"
import { evictBillingAccountQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseAccountFragment } from "@/v2-backoffice-client/license/account/__types__/license.account.fragment"

export type LicenseAccountReviewDialogProps = {
  account: LicenseAccountFragment
  open: boolean
  onOpenChange(open: boolean): void
}

// Acknowledges an unmatched account the mirror can never link on its own —
// intentional group billing (one entity pays for several customers; the
// children get operator licenses), a duplicate account finance is
// consolidating, a known not-a-customer. The note is the whole point: it
// tells the next operator why this row is fine, so the worklist only shows
// rows nobody has looked at.
function LicenseAccountReviewDialog(props: LicenseAccountReviewDialogProps) {
  const { account, open, onOpenChange } = props
  const [note, setNote] = useState(account.ReviewNote ?? "")

  const [reviewAccount, { loading, error }] = useReviewBillingAccountMutation({
    update: evictBillingAccountQueries,
    onCompleted(data) {
      const reviewed = Boolean(data.reviewBillingAccount.ReviewedAt)
      toast({
        variant: "success",
        title: reviewed ? "Account acknowledged" : "Acknowledgment cleared",
        description: reviewed
          ? `${account.CompanyName ?? "The account"} stays unmatched by design and is off the needs-review worklist.`
          : `${account.CompanyName ?? "The account"} is back on the needs-review worklist.`,
      })
      onOpenChange(false)
    },
    onError(mutationError) {
      toast({
        variant: "destructive",
        title: "Could not update the acknowledgment",
        description: mutationError.message,
      })
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            Acknowledge — {account.CompanyName ?? "billing account"}
          </Dialog.Title>
          <Dialog.Description>
            Marks this unmatched account as reviewed: the sharing or mismatch is
            known and intentional, so it leaves the needs-review worklist. The
            row keeps its reason, and the mirror still links it automatically if
            the ambiguity ever resolves (which clears this acknowledgment).
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-2">
          <Label htmlFor="license-account-review-note">Why is this fine?</Label>
          <Textarea
            id="license-account-review-note"
            value={note}
            maxLength={500}
            rows={3}
            placeholder="e.g. Group billing — this entity pays for the whole chain; the children hold operator licenses."
            onChange={(event) => {
              setNote(event.target.value)
            }}
          />
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          {account.ReviewedAt && (
            <Button
              type="button"
              variant="outline"
              isLoading={loading}
              data-feature-code="UI_BackofficeBillingAccount_Review_Clear"
              onClick={() => {
                void reviewAccount({
                  variables: {
                    accountId: account.Id,
                    isReviewed: false,
                    note: null,
                  },
                })
              }}
            >
              Clear Acknowledgment
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={note.trim().length === 0}
            data-feature-code="UI_BackofficeBillingAccount_Review_Save"
            onClick={() => {
              void reviewAccount({
                variables: {
                  accountId: account.Id,
                  isReviewed: true,
                  note: note.trim(),
                },
              })
            }}
          >
            Acknowledge
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseAccountReviewDialog
